// src/components/proposals/lineItemCategory.js
//
// What KIND of thing a foundational line item is — Labour, Hardware, Software
// or Other. Pure and React-free so the rules can be unit-tested without
// rendering anything.
//
// ── WHY THIS FIELD HAS TO EXIST ────────────────────────────────────────────
// A Fixed Price contract's HOURS are the block's LABOUR portion ÷ $170, while
// its PRICE is the block's full total. So the pipeline has to tell labour from
// hardware inside a block — and there was no signal at all to do it with.
//
// ⚠ IT IS NOT `type`. Quote line items carry a `type` field, which looks like
// the answer and is not: `toQuoteLineItem` hardcodes it to "hardware" for every
// item, mislabelling the Labour line itself. Foundational line items have no
// `type` at all. Reading either would have produced confidently wrong hours.
//
// ⚠ AND IT IS NOT THE DESCRIPTION TEXT. The only signal in the existing data is
// the word "Labour" appearing in free text. Matching on that would be exactly
// the string-matching this codebase banned after real cross-client incidents —
// and here it would be driving BILLABLE HOURS, which is a worse place for a
// guess than most. Text is used below to SUGGEST a category for a human to
// confirm, and never as the answer.

/* ⚠ ONE definition of the money maths, imported rather than mirrored.
   An earlier version of blockLabourTotal below multiplied by
   `Number(li.quantity) || 0`, where lineItemTotal uses `|| 1` — so a labour
   line with no quantity contributed NOTHING to the labour total while
   contributing its full price to the block total. Same numbers, two rules,
   silently disagreeing. */
import { lineItemTotal } from "./foundationalBlocks";

export const CATEGORIES = [
  { id: "labour",   label: "Labour" },
  { id: "hardware", label: "Hardware" },
  { id: "software", label: "Software" },
  { id: "other",    label: "Other" },
];
export const CATEGORY_IDS = CATEGORIES.map((c) => c.id);
export const DEFAULT_CATEGORY = "hardware";

export function categoryLabel(id) {
  return (CATEGORIES.find((c) => c.id === id) || {}).label || "";
}

export function isValidCategory(id) {
  return CATEGORY_IDS.includes(id);
}

/**
 * A SUGGESTED category, read from the item's own words.
 *
 * ⚠ THIS IS A SUGGESTION AND NOTHING ELSE. Everything it produces is stored
 * with `categoryConfirmed: false` and has to be looked at by a person before
 * it counts. That is the whole difference between this and the string-matching
 * the codebase forbids: the same fragile signal, used to save typing rather
 * than to decide a number.
 *
 * Deliberately narrow. A rule that guesses at everything produces a screen of
 * plausible-looking answers nobody reads, which is worse than a screen of
 * obvious blanks — so anything it is not reasonably sure of falls to the
 * default and is still flagged for review like everything else.
 */
export function suggestCategory(li = {}) {
  /* ⚠ A PART NUMBER BEATS ANY WORDING, and it is checked first for a reason.
     Run against the real 40 line items, an earlier version of this rule also
     read the `detail` text and matched service words in it — so "Dell Poweredge
     R360 1RU Hyper-V Host Server", whose detail describes its installation,
     came back as LABOUR. That block's labour total went to $21,355, which at
     $170/hr is 125 hours for a server that is plainly hardware. Two more
     products fell the same way.

     An item carrying a supplier part number is a thing being bought, whatever
     its blurb describes doing with it. The real labour lines carry no sku,
     supplier or manufacturer at all, so this cannot swallow them. */
  if (li.sku || li.manufacturer || li.supplierSku || li.supplier) return "hardware";

  /* ⚠ THE DESCRIPTION ONLY, never the detail. `detail` is prose about what the
     work involves, so it mentions installing and configuring on almost every
     line — including the hardware ones. The description is what the item IS. */
  const text = String(li.description || "").toLowerCase();

  if (/\b(labour|labor|installation|install|configure|configuration|setup|set up|project manage|consult|migration|onsite|on-site)\b/.test(text)) {
    return "labour";
  }
  // A licence or subscription is software even when a supplier ships it.
  if (/\b(licen[cs]e|subscription|microsoft 365|m365|office 365|antivirus|software)\b/.test(text)) {
    return "software";
  }
  return DEFAULT_CATEGORY;
}

/** Has a person actually confirmed this item's category? */
export function isConfirmed(li) {
  return !!(li && li.categoryConfirmed);
}

/** Does this item carry a category at all, confirmed or merely suggested? */
export function hasCategory(li) {
  return !!(li && isValidCategory(li.category));
}

/**
 * Give every line item in every block a suggested category.
 *
 * IDEMPOTENT, and never overwrites: an item that already has a category keeps
 * it, whether a person confirmed it or a previous pass suggested it. Re-running
 * changes nothing, which is what lets this run on load.
 *
 * Returns the new blocks, how many were suggested, and how many items are still
 * unconfirmed — the count the UI needs in order to ask for review.
 */
export function backfillCategories(blocks = []) {
  const list = Array.isArray(blocks) ? blocks : [];
  let suggested = 0;

  const next = list.map((b) => {
    const items = Array.isArray(b?.lineItems) ? b.lineItems : [];
    let touched = false;
    const lineItems = items.map((li) => {
      if (hasCategory(li)) return li;
      touched = true;
      suggested++;
      return { ...li, category: suggestCategory(li), categoryConfirmed: false };
    });
    return touched ? { ...b, lineItems } : b;
  });

  return {
    blocks: suggested ? next : list,
    suggested,
    unconfirmed: countUnconfirmed(suggested ? next : list),
  };
}

/** How many line items still carry an unreviewed suggestion. */
export function countUnconfirmed(blocks = []) {
  return (Array.isArray(blocks) ? blocks : []).reduce(
    (n, b) => n + (b?.lineItems || []).filter((li) => hasCategory(li) && !isConfirmed(li)).length,
    0);
}

/**
 * The labour money in one block — what the contract's HOURS are derived from.
 *
 * ⚠ Counts SUGGESTED items as well as confirmed ones, deliberately. Excluding
 * them would silently under-report hours on a block nobody had reviewed yet,
 * which is a wrong number wearing the appearance of a right one. Instead the
 * total is honest about what it is built from — `unconfirmed` comes back
 * alongside it so the caller can say so out loud rather than quietly
 * pretending the figure is settled.
 */
export function blockLabourTotal(block) {
  const items = (block?.lineItems || []).filter((li) => li.category === "labour");
  const total = items.reduce((sum, li) => sum + lineItemTotal(li), 0);
  return {
    total: Math.round(total * 100) / 100,
    unconfirmed: items.filter((li) => !isConfirmed(li)).length,
    lines: items.length,
  };
}
